// utils/storage.js

// Constantes pour les noms de bases de données
const DB_NAME = 'DrinkDeliveryApp';
const DB_VERSION = 3;

// Vérifier si IndexedDB est disponible
const isIndexedDBAvailable = () => {
  return 'indexedDB' in window;
};

// Ouvrir la base de données IndexedDB
const openDatabase = () => {
  return new Promise((resolve, reject) => {
    if (!isIndexedDBAvailable()) {
      reject(new Error('IndexedDB n\'est pas disponible'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Créer ou mettre à jour les object stores
      if (!db.objectStoreNames.contains('userSettings')) {
        db.createObjectStore('userSettings', { keyPath: 'id' });
      }
      
      if (!db.objectStoreNames.contains('products')) {
        const productsStore = db.createObjectStore('products', { keyPath: 'id' });
        productsStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('orders')) {
        const ordersStore = db.createObjectStore('orders', { keyPath: 'id' });
        ordersStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('pendingOrders')) {
        const pendingOrdersStore = db.createObjectStore('pendingOrders', { keyPath: 'id' });
        pendingOrdersStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('pendingActions')) {
        const pendingActionsStore = db.createObjectStore('pendingActions', { keyPath: 'id' });
        pendingActionsStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      if (!db.objectStoreNames.contains('orderSubscriptions')) {
        db.createObjectStore('orderSubscriptions', { keyPath: 'id' });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Récupérer un élément depuis le stockage
 * Essaie d'abord IndexedDB, puis fallback vers localStorage si nécessaire
 * @param {string} key - La clé de l'élément à récupérer
 * @returns {Promise<any>} - L'élément récupéré ou null
 */
export const getStorageItemAsync = async (key) => {
  try {
    if (isIndexedDBAvailable()) {
      const db = await openDatabase();
      const tx = db.transaction('userSettings', 'readonly');
      const store = tx.objectStore('userSettings');
      const request = store.get(key);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          if (request.result) {
            resolve(request.result.data);
          } else {
            // Fallback vers localStorage
            resolve(localStorage.getItem(key));
          }
        };
        
        request.onerror = () => {
          // Fallback vers localStorage
          resolve(localStorage.getItem(key));
        };
      });
    }
  } catch (error) {
    // console.error('Erreur lors de la récupération depuis IndexedDB:', error);
  }
  
  // Fallback vers localStorage si IndexedDB n'est pas disponible ou échoue
  return localStorage.getItem(key);
};

/**
 * Version synchrone de getStorageItemAsync pour une utilisation plus simple
 * @param {string} key - La clé de l'élément à récupérer
 * @returns {any} - L'élément récupéré ou null
 */
export const getStorageItem = (key) => {
  // Essayer d'abord localStorage comme solution rapide et synchrone
  return localStorage.getItem(key);
};

/**
 * Stocker un élément dans le stockage
 * Stocke à la fois dans IndexedDB et localStorage pour une meilleure disponibilité
 * @param {string} key - La clé de l'élément à stocker
 * @param {any} value - La valeur à stocker
 * @returns {Promise<boolean>} - true si le stockage a réussi
 */
export const setStorageItemAsync = async (key, value) => {
  // Essayer de stocker dans IndexedDB
  try {
    if (isIndexedDBAvailable()) {
      const db = await openDatabase();
      const tx = db.transaction('userSettings', 'readwrite');
      const store = tx.objectStore('userSettings');
      
      store.put({
        id: key,
        data: value,
        timestamp: Date.now()
      });
    }
  } catch (error) {
    // console.error('Erreur lors du stockage dans IndexedDB:', error);
  }
  
  // Toujours stocker dans localStorage comme fallback
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    // console.error('Erreur lors du stockage dans localStorage:', error);
    return false;
  }
};

/**
 * Version synchrone de setStorageItemAsync pour une utilisation plus simple
 * @param {string} key - La clé de l'élément à stocker
 * @param {any} value - La valeur à stocker
 * @returns {boolean} - true si le stockage a réussi
 */
export const setStorageItem = (key, value) => {
  // Stocker immédiatement dans localStorage
  try {
    localStorage.setItem(key, value);
    
    // Lancer un appel asynchrone à IndexedDB sans attendre le résultat
    setStorageItemAsync(key, value).catch(error => {
      // console.error('Erreur lors du stockage asynchrone:', error);
    });
    
    return true;
  } catch (error) {
    // console.error('Erreur lors du stockage dans localStorage:', error);
    return false;
  }
};

/**
 * Supprimer un élément du stockage
 * @param {string} key - La clé de l'élément à supprimer
 * @returns {Promise<boolean>} - true si la suppression a réussi
 */
export const removeStorageItemAsync = async (key) => {
  // Essayer de supprimer depuis IndexedDB
  try {
    if (isIndexedDBAvailable()) {
      const db = await openDatabase();
      const tx = db.transaction('userSettings', 'readwrite');
      const store = tx.objectStore('userSettings');
      store.delete(key);
    }
  } catch (error) {
    // console.error('Erreur lors de la suppression depuis IndexedDB:', error);
  }
  
  // Toujours supprimer depuis localStorage
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    // console.error('Erreur lors de la suppression depuis localStorage:', error);
    return false;
  }
};

/**
 * Version synchrone de removeStorageItemAsync
 * @param {string} key - La clé de l'élément à supprimer
 * @returns {boolean} - true si la suppression a réussi
 */
export const removeStorageItem = (key) => {
  // Supprimer immédiatement depuis localStorage
  try {
    localStorage.removeItem(key);
    
    // Lancer un appel asynchrone à IndexedDB
    removeStorageItemAsync(key).catch(error => {
      // console.error('Erreur lors de la suppression asynchrone:', error);
    });
    
    return true;
  } catch (error) {
    // console.error('Erreur lors de la suppression depuis localStorage:', error);
    return false;
  }
};

/**
 * Effacer toutes les données stockées
 * @returns {Promise<boolean>} - true si l'opération a réussi
 */
export const clearStorageAsync = async () => {
  // Essayer de vider IndexedDB
  try {
    if (isIndexedDBAvailable()) {
      const db = await openDatabase();
      const stores = Array.from(db.objectStoreNames);
      
      for (const storeName of stores) {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        store.clear();
      }
    }
  } catch (error) {
    // console.error('Erreur lors du vidage d\'IndexedDB:', error);
  }
  
  // Toujours vider localStorage
  try {
    localStorage.clear();
    return true;
  } catch (error) {
    // console.error('Erreur lors du vidage de localStorage:', error);
    return false;
  }
};

/**
 * Version synchrone de clearStorageAsync
 * @returns {boolean} - true si l'opération a réussi
 */
export const clearStorage = () => {
  // Vider immédiatement localStorage
  try {
    localStorage.clear();
    
    // Lancer un appel asynchrone pour vider IndexedDB
    clearStorageAsync().catch(error => {
      // console.error('Erreur lors du vidage asynchrone:', error);
    });
    
    return true;
  } catch (error) {
    // console.error('Erreur lors du vidage de localStorage:', error);
    return false;
  }
};

// Exporter les fonctions et constantes utiles
export {
  DB_NAME,
  DB_VERSION,
  isIndexedDBAvailable,
  openDatabase
};